import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { KanbanPageV13 } from "./KanbanPageV13";

const KANBAN_REFRESH_EVENT = "inksight:kanban-refresh-needed";
const COMPLETE_PHASE_ID = "graphics_complete";
const SHOW_COMPLETED_KEY = "inksight-kanban-show-completed";
const DEFAULT_ALL_PROJECT_ROLES = new Set(["graphics_admin", "sales_manager"]);

const shouldDefaultToAllProjects = (user, canViewAllProjects) => Boolean(
  canViewAllProjects
  && DEFAULT_ALL_PROJECT_ROLES.has(String(user?.role || ""))
);

const showCompletedEnabled = () => {
  try {
    return sessionStorage.getItem(SHOW_COMPLETED_KEY) === "true";
  } catch {
    return false;
  }
};

const persistShowCompleted = (value) => {
  try {
    sessionStorage.setItem(SHOW_COMPLETED_KEY, value ? "true" : "false");
  } catch {
    // Session storage can fail in locked-down browsers; keep the in-memory state.
  }
};

const hasProjectLevelAttention = (project) => Boolean(
  project?.last_activity_needs_attention
  || project?.last_activity_requires_response
  || project?.has_attention_activity
  || project?.has_unread_response_activity
  || project?.attention_activity_count
  || project?.unread_response_activity_count,
);

const attentionCountFor = (project) => Number(
  project?.attention_activity_count
  || project?.unread_response_activity_count
  || (project?.has_unread_response_activity ? 1 : 0)
  || (project?.last_activity_needs_attention ? 1 : 0)
  || (project?.last_activity_requires_response ? 1 : 0)
  || 0,
);

const attentionLabelFor = (project) => {
  const count = attentionCountFor(project);
  if (!hasProjectLevelAttention(project)) return project?.sub_status || "";

  const target = String(project?.last_activity_attention_target || project?.attention_target || project?.activity_attention_target || "").toLowerCase();
  const prefix = target === "sales"
    ? "❗ Needs Sales"
    : target === "graphics"
      ? "❗ Needs Graphics"
      : target === "both"
        ? "❗ Needs Sales + Graphics"
        : "❗ Needs Attention";
  const existing = String(project.sub_status || "").trim();

  if (!existing) return count > 1 ? `${prefix} (${count})` : prefix;
  if (existing.includes("Needs Attention") || existing.includes("Needs Sales") || existing.includes("Needs Graphics")) return existing;
  return `${existing} • ${prefix}${count > 1 ? ` (${count})` : ""}`;
};

const attentionNameFor = (project) => {
  const name = String(project?.name || "Project").trim();
  if (!hasProjectLevelAttention(project)) return name;
  if (name.startsWith("❗")) return name;
  return `❗ ${name}`;
};

const hideCompletedFromNormalQueue = (response) => {
  if (!response?.config?.url?.includes("/workflow/kanban")) return response;
  const params = new URLSearchParams(window.location.search);
  const explicitlyViewingCompleted = params.get("phase") === COMPLETE_PHASE_ID;
  if (explicitlyViewingCompleted || showCompletedEnabled()) return response;

  if (Array.isArray(response.data?.projects)) {
    response.data.projects = response.data.projects.filter((project) => project.workflow_phase !== COMPLETE_PHASE_ID);
  }

  if (Array.isArray(response.data?.phases)) {
    response.data.phases = response.data.phases.filter((phase) => phase.id !== COMPLETE_PHASE_ID);
  }

  return response;
};

const annotateActivityFlags = (response) => {
  if (!response?.config?.url?.includes("/workflow/kanban")) return response;
  if (!Array.isArray(response.data?.projects)) return response;

  response.data.projects = response.data.projects.map((project) => {
    if (!hasProjectLevelAttention(project)) return project;
    return {
      ...project,
      name: attentionNameFor(project),
      sub_status: attentionLabelFor(project),
      activity_queue_flag: "needs_attention",
      has_attention_activity: true,
    };
  });

  return response;
};

export const KanbanPageV14 = () => {
  const [reloadKey, setReloadKey] = useState(0);
  const [interceptorReady, setInterceptorReady] = useState(false);
  const [showCompleted, setShowCompleted] = useState(showCompletedEnabled);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, canViewAllProjects } = useAuth();
  const defaultAllReady = !shouldDefaultToAllProjects(user, canViewAllProjects) || searchParams.has("view");

  useEffect(() => {
    if (defaultAllReady) return;
    const next = new URLSearchParams(searchParams);
    next.set("view", "all");
    setSearchParams(next, { replace: true });
  }, [defaultAllReady, searchParams, setSearchParams]);

  useEffect(() => {
    const refreshQueue = () => {
      setReloadKey((value) => value + 1);
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshQueue();
    };

    const interceptor = axios.interceptors.response.use((response) => {
      hideCompletedFromNormalQueue(response);
      annotateActivityFlags(response);
      if (
        response.data?.kanban_group_move === true
        || response.data?.group_color_change === true
      ) {
        window.setTimeout(refreshQueue, 0);
      }
      return response;
    });

    // Do not mount the queue until the interceptor exists. Otherwise the first
    // /workflow/kanban load can complete before attention markers are annotated.
    setInterceptorReady(true);

    window.addEventListener(KANBAN_REFRESH_EVENT, refreshQueue);
    window.addEventListener("focus", refreshQueue);
    window.addEventListener("pageshow", refreshQueue);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      axios.interceptors.response.eject(interceptor);
      window.removeEventListener(KANBAN_REFRESH_EVENT, refreshQueue);
      window.removeEventListener("focus", refreshQueue);
      window.removeEventListener("pageshow", refreshQueue);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  const toggleShowCompleted = () => {
    const next = !showCompleted;
    persistShowCompleted(next);
    setShowCompleted(next);
    setReloadKey((value) => value + 1);
  };

  if (!interceptorReady || !defaultAllReady) return null;

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={toggleShowCompleted}
          className={`rounded-lg border px-4 py-2 text-sm font-medium ${showCompleted ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-200" : "border-border bg-secondary text-muted-foreground hover:bg-secondary/80"}`}
          title={showCompleted ? "Hide completed projects from the active Queue" : "Show completed projects in the Queue"}
        >
          {showCompleted ? "Hide Completed" : "Show Completed"}
        </button>
      </div>
      <KanbanPageV13 key={`${reloadKey}-${showCompleted ? "completed" : "active"}`} />
    </div>
  );
};